import React, { useState, useContext, Dispatch, SetStateAction, ChangeEvent } from 'react';
import { Button } from 'react-bootstrap';
import CommonConfigDialog from './CommonConfigDialog';
import { RequiredDialogInput, DialogInput, RequiredDialogSelect, DialogSelectWithMoreConfig, DialogCheckbox, DialogIntegerInput } from '../../DialogInputs';
import FunctionParamsDialog from './FunctionParamsDialog';
import { handleInputChange } from '../../Utilities/DialogUtils';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGears } from '@fortawesome/free-solid-svg-icons';
import { useCalcFunctions, useLabFunctions, useOffspecFunctions } from '../../Utilities/Hooks';
import useAlert from '../../Alerts/useAlert';
import PrivilegeContext from '../../Contexts/PrivilegeContext';
import { BusinessUnit } from '../../Types/BusinessUnit';
import { Quality } from '../../Types/ConfigurationQuality';
import { QualityFunction } from '../../Types/QualityFunction';

interface  QualityEditDialogProps {
    dialogData: Quality,
    setDialogData: Dispatch<SetStateAction<Quality>>,
    showDialog: boolean,
    closeDialog: () => void,
    businessUnits: BusinessUnit[]
}

type QualityFunctionProperties =
    'transferCalculateFunction' |
    'transferIsOffspecFunction' |
    'batchCalculateFunction' |
    'batchLabCalculateFunction' |
    'lotCalculateFunction' |
    'lotLabCalculateFunction' |
    'lotIsOffspecFunction';

function QualityEditDialog({ dialogData, setDialogData, showDialog, closeDialog, businessUnits }: QualityEditDialogProps) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const [fieldErrors, setFieldErrors] = useState<{ [K in keyof Quality]?: string; }>({});
    const calcFunctions: QualityFunction[] = useCalcFunctions();
    const labFunctions: QualityFunction[] = useLabFunctions();
    const offspecFunctions: QualityFunction[] = useOffspecFunctions();

    const [loading, setLoading] = useState<boolean>(false);
    const { canConfigure } = useContext(PrivilegeContext);

    const saveQuality = (quality: Quality) => {
        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/quality`;
            if (quality.qualityId != null) {
                url += `/${quality.qualityId}`;
            }

            if (quality.defaultDecimalPlaces === "") {
                quality.defaultDecimalPlaces = null;
            }

            if (quality.specificGravity === "") {
                quality.specificGravity = null;
            }

            if (quality.displayOrder === "") {
                quality.displayOrder = null;
            }

            fetchDataAuthenticated(url, "POST", JSON.stringify(quality))
                .then(response => {
                    if (!response.ok) {
                        return response.json().then(error => Promise.reject(error));
                    }
                    return response;
                })
                .then(response => response.json())
                .then(data => {
                    if (data.errors !== null && data.errors !== undefined) {
                        reject(data);
                    }
                    resolve(data);
                })
                .catch(error => {
                    setAlert("danger", "Error", `Failed To Create Quality : \n${error.detail}`);
                });

        });

        return promise;
    };

    const onChange = (e: unknown) => {
        handleInputChange(e, dialogData, setDialogData, fieldErrors, setFieldErrors);
    };

    const getFunctionNameOnly = (functionNameWithParams: string | undefined | null) => {
        if (functionNameWithParams === undefined || functionNameWithParams == null)
            return functionNameWithParams;

        let braceIndex = functionNameWithParams.indexOf("(");
        if (braceIndex === -1)
            return functionNameWithParams;

        return functionNameWithParams.substr(0, braceIndex);
    };

    const getMatchingFunction = (functions: QualityFunction[], findFunctionName: string | undefined) => {
        if (functions === undefined)
            return null;

        let findFunctionNameWithoutParams = getFunctionNameOnly(findFunctionName);

        for (let idx = 0; idx < functions.length; idx++) {
            let func = functions[idx];
            let funcNameWithoutParams = getFunctionNameOnly(func.name);

            if (findFunctionNameWithoutParams === funcNameWithoutParams) {
                return func;
            }
        }

        return null;
    };

    const getFunctionParameterValues = (functionNameWithParams: string | undefined) => {
        if (functionNameWithParams === undefined || functionNameWithParams == null)
            return [];

        let braceIndex = functionNameWithParams.indexOf("(");
        if (braceIndex === -1)
            return [];

        let paramsPart = functionNameWithParams.substring(braceIndex + 1, functionNameWithParams.length - 1);

        return paramsPart.split(",");
    };

    const FunctionParamsCell = ({ func, fieldName }: { func: QualityFunction, fieldName: QualityFunctionProperties }) => {
        const [showFunctionParams, setShowFunctionParams] = useState<boolean>(false);
        let initialParameterValues = getFunctionParameterValues(dialogData[fieldName]);
        const [parameters, setParameters] = useState(initialParameterValues);

        const SaveParams = (params: string[]) => {
            // Put the parameters back into a full function name string + args in the dialog data

            // Common dialog save expects a promise.. We can 'reject' here with field errors if we want
            let promise = new Promise((resolve, reject) => {
                // NB, params is an object if its been modified, it it hasn't (user just pressed ok) then its an array..
                let functionNameWithParams = func.name + "(";

                for (var paramIdx = 0; paramIdx < func.parameters.length; paramIdx++) {
                    if (paramIdx > 0)
                        functionNameWithParams += ",";

                    const defaultValue = func.parameters[paramIdx].type === 'Boolean' ? false : undefined
                    functionNameWithParams += params[paramIdx] ?? defaultValue;
                }
                functionNameWithParams += ")";

                // Anything to validate in here?

                setFieldErrors({
                    ...fieldErrors,
                    [fieldName]: null
                });

                setDialogData({
                    ...dialogData,
                    [fieldName]: functionNameWithParams
                });

                let fieldDOM: HTMLObjectElement | null = document.querySelector("[name=" + fieldName + "]");
                fieldDOM?.setCustomValidity("");

                resolve(functionNameWithParams);
            });

            return promise;
        };

        return (
            <Button variant="outline-secondary">
                <FontAwesomeIcon className="link" icon={faGears} onClick={() => setShowFunctionParams(true)} />
                <FunctionParamsDialog fieldName={fieldName} func={func} showDialog={showFunctionParams} parameters={parameters} setParameters={setParameters} closeDialog={() => setShowFunctionParams(false)} save={SaveParams} />
            </Button>
        );
    }

    const onChangeFunctionSelect = (functions: QualityFunction[]) => (e: ChangeEvent<HTMLInputElement>) => {
        let field = e.currentTarget.name;

        let value = e.currentTarget.value;
        let matchingFunc = getMatchingFunction(functions, value);
        let params = getFunctionParameterValues(value);

        e.currentTarget.setCustomValidity("");

        setDialogData({
            ...dialogData,
            [field]: value
        });

        // Validate selection
        if (matchingFunc?.parameters.length !== params.length) {
            e.currentTarget.setCustomValidity("Invalid arguments supplied");
        }

        if (e.currentTarget.validity.valid) {
            setFieldErrors({
                ...fieldErrors,
                [field]: null
            });
        }
        else {
            setFieldErrors({
                ...fieldErrors,
                [field]: e.currentTarget.validationMessage
            });
        }
    };

    const FunctionSelect = ({ controlId, title, name, functions }: { controlId: string, title: string, name: QualityFunctionProperties, functions: QualityFunction[] }) => {
        let matchingFunc = getMatchingFunction(functions, dialogData[name]);
        let selectedFnhasArgs = (matchingFunc != null && matchingFunc.parameters.length > 0);

        let functionConfig = selectedFnhasArgs ? <FunctionParamsCell func={matchingFunc!} fieldName={name}></FunctionParamsCell> : null;

        return (
            <DialogSelectWithMoreConfig controlId={controlId} title={title} name={name} additionalConfig={functionConfig} value={getFunctionNameOnly(dialogData[name])} error={fieldErrors[name]} onChange={onChangeFunctionSelect(functions)}>
                <option value="" key={0}>Please Select...</option>
                {functions.map((func, index) => <option value={func.name} key={index}>{(matchingFunc !== null) && (matchingFunc.name === func.name) ? dialogData[name] : func.name}</option>)}
            </DialogSelectWithMoreConfig>
        );
    };

    const cloneRecipe = async () => {
        setLoading(true);
        let url = `api/v1/quality/${dialogData.qualityId}/clone`;

        await fetchDataAuthenticated(url, "POST")
            .then(async response => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error(await response.text());
            })
            .then(data => {
                setDialogData({ ...data });
                setAlert("success", "Copied", "Quality has been copied");
            })
            .catch(error => {
                setAlert("danger", "Error", `Failed to clone quality. ${error}`);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const CloneQualityButton = () => {
        return canConfigure
            ?
            <Button className="me-auto" variant="secondary" onClick={cloneRecipe}>
                Clone Quality
            </Button>
            : undefined;
    };

    return (
        <CommonConfigDialog title={"Quality - " + dialogData.name} dialogData={dialogData} showDialog={showDialog} closeDialog={closeDialog} save={saveQuality} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors} wideDialog={true} FooterButtons={CloneQualityButton}>
            <RequiredDialogInput controlId="formQualityName" title="Name" name="name" value={dialogData.name} error={fieldErrors.name} onChange={onChange} />
            <DialogInput controlId="formQualityDescription" title="Description" name="description" value={dialogData.description} error={fieldErrors.description} onChange={onChange} />
            <DialogInput controlId="formQualityType" title="Type" name="type" value={dialogData.type} error={fieldErrors.type} onChange={onChange} />
            <DialogInput controlId="formQualityUnits" title="Units" name="units" value={dialogData.units} error={fieldErrors.units} onChange={onChange} />
            <DialogCheckbox controlId="formQualityIsNumeric" title="Numeric?" name="isNumeric" checked={dialogData.isNumeric} error={fieldErrors.isNumeric} onChange={onChange} disabled={false} />
            <DialogIntegerInput controlId="formQualityDefaultDPs" title="Default DPs" name="defaultDecimalPlaces" value={dialogData.defaultDecimalPlaces} error={fieldErrors.defaultDecimalPlaces} onChange={onChange} placeholder={undefined} />
            <RequiredDialogInput controlId="formQualityDefaultValue" title="Default Value To Use" name="defaultValueToUse" value={dialogData.defaultValueToUse} error={fieldErrors.defaultValueToUse} onChange={onChange} />
            <DialogInput controlId="formQualityAnalysis" title="Analysis" name="analysis" value={dialogData.analysis} error={fieldErrors.analysis} onChange={onChange} helpText="Analysis configured for this quality at L3" />
            <DialogInput controlId="formQualityComponent" title="Component" name="component" value={dialogData.component} error={fieldErrors.component} onChange={onChange} helpText="Component configured for this quality at L3" />
            <DialogInput controlId="formQualityReleaseAnalysis" title="Release Analysis" name="releaseAnalysis" value={dialogData.releaseAnalysis} error={fieldErrors.releaseAnalysis} onChange={onChange} helpText="Analysis configured for this quality in Sample Manager for the release Sample" />
            <DialogInput controlId="formQualityReleaseComponent" title="Release Component" name="releaseComponent" value={dialogData.releaseComponent} error={fieldErrors.releaseComponent} onChange={onChange} helpText="Component configured for this quality in Sample Manager for the release Sample" />
            <DialogIntegerInput controlId="formQualityDisplayOrder" title="Display Order" name="displayOrder" value={dialogData.displayOrder} error={fieldErrors.displayOrder} onChange={onChange} placeholder={undefined} />
            <DialogInput controlId="formQualityMin" title="Min Value" name="masterMinValue" value={dialogData.masterMinValue} error={fieldErrors.masterMinValue} onChange={onChange} />
            <DialogInput controlId="formQualityMax" title="Max Value" name="masterMaxValue" value={dialogData.masterMaxValue} error={fieldErrors.masterMaxValue} onChange={onChange} />
            <DialogInput controlId="formQualitySpecificGravity" title="Specific Gravity" name="specificGravity" value={dialogData.specificGravity} error={fieldErrors.specificGravity} onChange={onChange} />
            <RequiredDialogInput controlId="formQualityConfidenceValueMinMD" title="Confidence Value Min MD" name="confidenceValueMinMD" value={dialogData.confidenceValueMinMD} error={fieldErrors.confidenceValueMinMD} onChange={onChange} />
            <FunctionSelect controlId="formQualityTransferCalc" title="Transfer Calc Func" name="transferCalculateFunction" functions={calcFunctions} />
            <FunctionSelect controlId="formQualityTransferOffspec" title="Transfer Offspec Func" name="transferIsOffspecFunction" functions={offspecFunctions} />
            <FunctionSelect controlId="formQualityBatchCalc" title="Batch Calc Func" name="batchCalculateFunction" functions={calcFunctions} />
            <FunctionSelect controlId="formQualityBatchLabCalc" title="Batch Lab Calc Func" name="batchLabCalculateFunction" functions={labFunctions} />
            <FunctionSelect controlId="formQualityLotCalc" title="Lot Calc Func" name="lotCalculateFunction" functions={calcFunctions} />
            <FunctionSelect controlId="formQualityLotLabCalc" title="Lot Lab Calc Func" name="lotLabCalculateFunction" functions={labFunctions} />
            <FunctionSelect controlId="formQualityLotOffspec" title="Lot Offspec Func" name="lotIsOffspecFunction" functions={offspecFunctions} />
            <RequiredDialogSelect controlId="formQualityBusinessUnit" title="Business Unit" name="businessUnitId" value={dialogData.businessUnitId} error={fieldErrors.businessUnitId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {businessUnits.map((bu) => <option value={bu.businessUnitId} key={bu.businessUnitId}>{bu.name}</option>)}
            </RequiredDialogSelect>
            <DialogCheckbox disabled={dialogData.qualityId === null} controlId="formQualityIsDeleted" title="Deleted?" name="isDeleted" checked={dialogData.isDeleted} error={fieldErrors.isDeleted} onChange={onChange} />
        </CommonConfigDialog>
    );
}

export default QualityEditDialog;